import React, { useState, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Smartphone, Mail, User, Calendar, Flag } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PinInput } from '@/components/ui/PinInput'
import { PhoneInput } from '@/components/ui/PhoneInput'
import { RadioGroup } from '@/components/ui/RadioButton'
import { GlobalHeader } from '@/components/GlobalHeader'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Country } from '@/data/countries'
import { worldCountries, getCountryName, findCountryByCode } from '@/data/worldCountries'
import { Select } from '@/components/ui/Select'
import { TermsModal } from '@/components/ui/TermsModal'
import { PrivacyModal } from '@/components/ui/PrivacyModal'
import { KYCWelcomeModal } from '@/components/KYCWelcomeModal'
import { BusinessOnboardingModal } from '@/components/BusinessOnboardingModal'
import toast from 'react-hot-toast'

interface RegisterFormData {
  phone_number: string
  email: string
  password: string
  password_confirmation: string
  user_type: 'individual' | 'business' | 'tourist'
  first_name?: string
  last_name?: string
  date_of_birth?: string
  nationality?: string
}

function RegisterPageContent() {
  const { t, i18n } = useTranslation()
  const [showPin, setShowPin] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>()
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false)
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false)
  const [registeredUserType, setRegisteredUserType] = useState<RegisterFormData['user_type'] | null>(null)
  const setModalOpenRef = React.useRef<React.Dispatch<React.SetStateAction<boolean>> | null>(null)
  
  const { register: registerUser, login } = useAuthStore()
  const navigate = useNavigate()
  
  // Store the setter function in a ref so it's always accessible
  React.useEffect(() => {
    setModalOpenRef.current = setIsKYCModalOpen
  }, [])
  
  // Listen for custom event to open modal
  React.useEffect(() => {
    const handleOpenModalEvent = () => {
      setIsKYCModalOpen(true)
    }
    
    window.addEventListener('openKYCModal', handleOpenModalEvent)
    return () => {
      window.removeEventListener('openKYCModal', handleOpenModalEvent)
    }
  }, [])
  
  // Add global error handler for unhandled promise rejections
  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason)
      event.preventDefault() // Prevent the default browser behavior
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<RegisterFormData>({
    mode: 'onBlur' // Change from onChange to onBlur for better performance
  })

  const password = watch('password')
  const password_confirmation = watch('password_confirmation')
  const nationality = watch('nationality')

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Validate user type
      if (!data.user_type) {
        toast.error(t('auth.userTypeRequired', 'Tipo de usuário é obrigatório'))
        return
      }

      // Validate password match
      if (data.password !== data.password_confirmation) {
        toast.error(t('auth.passwordMismatch', 'Senhas não coincidem'))
        return
      }

      // Validate password length
      if (data.password.length < 6) {
        toast.error(t('auth.passwordLength', 'Senha deve ter pelo menos 6 caracteres'))
        return
      }

      // Validate phone number exists
      if (!data.phone_number || data.phone_number.trim().length === 0) {
        toast.error(t('auth.invalidPhone', 'Número de telefone inválido'))
        return
      }

      // Validate age (must be at least 18)
      if (!data.date_of_birth) {
        toast.error(t('auth.dateOfBirthRequired', 'Data de nascimento é obrigatória'))
        return
      }
      const birthDate = new Date(data.date_of_birth)
      const today = new Date()
      const age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      
      if (age < 18 || (age === 18 && monthDiff < 0)) {
        toast.error(t('auth.ageRequirement', 'Deve ter pelo menos 18 anos'))
        return
      }

      setIsLoading(true)
      setRegisteredUserType(data.user_type)
      
      // Normalize phone number - ensure it starts with + and remove spaces
      let normalizedPhone = data.phone_number.trim()
      // Remove all spaces but keep + and digits
      normalizedPhone = normalizedPhone.replace(/\s/g, '')
      
      // Ensure it starts with +
      if (!normalizedPhone.startsWith('+')) {
        // If it doesn't start with +, try to add it based on common patterns
        if (normalizedPhone.startsWith('239')) {
          normalizedPhone = '+' + normalizedPhone
        } else if (normalizedPhone.startsWith('351')) {
          normalizedPhone = '+' + normalizedPhone
        } else if (normalizedPhone.length >= 9) {
          // If it's a 9-digit number, assume it's São Tomé
          normalizedPhone = '+239' + normalizedPhone
        } else {
          // Last resort: add + to whatever we have
          normalizedPhone = '+' + normalizedPhone.replace(/^\+/, '')
        }
      }
      
      // Final validation - ensure it has at least + and country code + number
      if (!normalizedPhone.startsWith('+')) {
        toast.error(t('auth.invalidPhone', 'Número de telefone deve começar com + seguido do código do país'))
        setIsLoading(false)
        return
      }
      
      if (normalizedPhone.length < 10) {
        toast.error(t('auth.invalidPhone', 'Número de telefone inválido'))
        setIsLoading(false)
        return
      }
      
      // Determine currency based on user type
      let currency: 'EUR' | 'STN' | 'USD' = 'EUR' // default
      if (data.user_type === 'tourist') {
        currency = 'EUR'
      } else if (data.user_type === 'individual') {
        currency = 'STN'
      } else if (data.user_type === 'business') {
        currency = 'STN' // business also uses STN
      }
      
      // Prepare data according to API specification
      const registerData = {
        phone_number: normalizedPhone,
        email: data.email.trim(),
        password: data.password,
        password_confirmation: data.password_confirmation,
        user_type: data.user_type,
        first_name: data.first_name?.trim(),
        last_name: data.last_name?.trim(),
        date_of_birth: data.date_of_birth,
        nationality: data.nationality,
        currency: currency
      }
      
      // Register user directly
      await registerUser(registerData)

      // Login immediately after registration to ensure KYC requests are authenticated
      try {
        await login({
          phone_number: normalizedPhone,
          password: data.password
        })
      } catch (loginError) {
        toast.error(t('auth.loginAfterRegisterFailed', 'Falha ao iniciar sessão. Tenta fazer login.'))
        setIsLoading(false)
        return
      }

      toast.success(t('auth.registerSuccess', 'Conta criada com sucesso!'))
      
      // Wait a bit to ensure state is stable before opening modal
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Use flushSync to force synchronous state update
      try {
        flushSync(() => {
          setIsKYCModalOpen(true)
        })
      } catch (error) {
        console.error('Error in flushSync:', error)
        // Fallback to regular state update
        setIsKYCModalOpen(true)
      }
      
      // Also dispatch custom event as backup
      window.dispatchEvent(new CustomEvent('openKYCModal'))
      
      // Double-check after a microtask
      setTimeout(() => {
        if (setModalOpenRef.current) {
          setModalOpenRef.current((prev) => {
            if (!prev) {
              return true
            }
            return prev
          })
        }
      }, 50)
    } catch (error: any) {
      // Get error message from API
      const apiError = error.response?.data?.error || error.response?.data?.message || ''
      
      // Map backend error messages to translated messages
      let errorMessage: string
      if (apiError.toLowerCase().includes('email already exists') || 
          apiError.toLowerCase().includes('email já está') ||
          apiError.toLowerCase().includes('email already registered')) {
        errorMessage = t('auth.emailAlreadyExists')
      } else if (apiError.toLowerCase().includes('phone number already exists') || 
                 apiError.toLowerCase().includes('phone already exists') ||
                 apiError.toLowerCase().includes('número de telefone já') ||
                 apiError.toLowerCase().includes('phone number already registered')) {
        errorMessage = t('auth.phoneAlreadyExists')
      } else if (apiError.toLowerCase().includes('invalid user type')) {
        errorMessage = t('auth.userTypeRequired')
      } else if (apiError.toLowerCase().includes('email, phone number and password are required') ||
                 apiError.toLowerCase().includes('email, número de telefone e palavra-passe são obrigatórios')) {
        errorMessage = t('auth.registerError')
      } else if (apiError) {
        // Use API error message if it's a specific error we haven't mapped
        errorMessage = apiError
      } else {
        // Fallback to generic error
        errorMessage = t('auth.registerError')
      }
      
      toast.error(errorMessage)
      console.error('Register error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKYCComplete = () => {
    setIsKYCModalOpen(false)
    if (registeredUserType === 'business') {
      setIsBusinessModalOpen(true)
      return
    }
    setTimeout(() => {
      navigate('/')
    }, 100)
  }

  const handleKYCClose = () => {
    setIsKYCModalOpen(false)
    setTimeout(() => {
      navigate('/')
    }, 100)
  }


  const handlePhoneChange = useCallback((phoneNumber: string, country: Country) => {
    try {
      // Normalize phone number - remove spaces
      let normalizedPhone = phoneNumber.trim().replace(/\s/g, '')
      
      // Ensure it starts with + (phoneCode from PhoneInput should already include +)
      if (!normalizedPhone.startsWith('+') && country?.phoneCode) {
        // If phoneCode already has +, use it directly
        if (country.phoneCode.startsWith('+')) {
          // Extract just the number part (remove country code if present)
          const numberPart = normalizedPhone.replace(country.phoneCode.replace('+', ''), '')
          normalizedPhone = country.phoneCode + numberPart
        } else {
          normalizedPhone = '+' + normalizedPhone
        }
      }
      
      // Use setTimeout to avoid synchronous state updates that might cause issues
      setTimeout(() => {
        try {
          setValue('phone_number', normalizedPhone, { shouldValidate: false })
          
          // Only update nationality and user type when country actually changes
          const countryChanged = selectedCountry?.code !== country.code
          
          if (countryChanged) {
            setSelectedCountry(country)
            
            // Auto-fill nationality when country changes
            if (country) {
              // Find the world country by code to get the nationality
              const worldCountry = worldCountries.find(wc => wc.code === country.code)
              if (worldCountry) {
                setValue('nationality', worldCountry.nationality, { shouldValidate: false, shouldDirty: true })
                
                // Auto-set user_type based on country
                if (country.code === 'ST') {
                  // São Tomé e Príncipe - keep current selection or default to individual
                  const currentUserType = watch('user_type')
                  if (!currentUserType) {
                    setValue('user_type', 'individual', { shouldValidate: false, shouldDirty: true })
                  }
                } else {
                  // Any other country - set to tourist
                  setValue('user_type', 'tourist', { shouldValidate: false, shouldDirty: true })
                  // Show info toast for user
                  toast.success(t('auth.userTypeSetToTourist', 'Tipo de usuário definido como Turista Internacional'))
                }
              }
            }
          }
        } catch (innerError) {
          console.error('Error in handlePhoneChange inner:', innerError)
        }
      }, 0)
    } catch (error) {
      console.error('Error in handlePhoneChange:', error)
    }
  }, [setValue, watch, t, selectedCountry])


  // PIN validation is handled in onSubmit function for better performance

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background-light to-secondary/5">
      {/* Global Header with Language Selector */}
      <GlobalHeader showNavigation={false} />
      
      <div className="flex items-center justify-center p-4 pt-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mx-auto mb-4"
          >
            <img 
              src="/assets/djelo-sem-fundo.png" 
              alt="djelo" 
              className="w-52 h-auto object-contain mx-auto mb-5"
            />
          </motion.div>
          <p className="text-text-secondary">{t('common.walletDescription', 'Sua carteira digital em São Tomé e Príncipe')}</p>
        </div>

        {/* Register Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-6"
        >
          <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
            {t('auth.register')}
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Personal Information */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="text"
                placeholder={t('auth.firstName', 'Nome')}
                {...register('first_name', { 
                  required: t('auth.firstNameRequired', 'Nome é obrigatório'),
                  minLength: { value: 2, message: t('auth.firstNameMinLength', 'Nome deve ter pelo menos 2 caracteres') },
                  pattern: {
                    value: /^[a-zA-ZÀ-ÿ\s]+$/,
                    message: t('auth.nameInvalid', 'Nome deve conter apenas letras')
                  }
                })}
                error={errors.first_name?.message}
              />
              <Input
                type="text"
                placeholder={t('auth.lastName', 'Apelido')}
                {...register('last_name', { 
                  required: t('auth.lastNameRequired', 'Apelido é obrigatório'),
                  minLength: { value: 2, message: t('auth.lastNameMinLength', 'Apelido deve ter pelo menos 2 caracteres') },
                  pattern: {
                    value: /^[a-zA-ZÀ-ÿ\s]+$/,
                    message: t('auth.nameInvalid', 'Apelido deve conter apenas letras')
                  }
                })}
                error={errors.last_name?.message}
              />
            </div>

            {/* Contact Information */}
            <PhoneInput
              onChange={handlePhoneChange}
              placeholder={t('auth.phonePlaceholder', 'Número de telefone')}
              error={errors.phone_number?.message}
              disabled={isLoading}
            />

            <div className="space-y-1">
            <label className="text-sm font-medium text-text-primary">
                E-mail
              </label>
            
            <Input
              type="email"
              placeholder={t('auth.emailPlaceholder', 'seu@email.com')}
              {...register('email', { 
                required: t('auth.emailRequired', 'Email é obrigatório'),
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t('auth.invalidEmail', 'Email inválido')
                }
              })}
              error={errors.email?.message}
            />
            </div>
            <div className="space-y-1">

            {/* Date of Birth */}
            <label className="text-sm font-medium text-text-primary">
                {t('auth.dateOfBirth', 'Data de nascimento')}
              </label>
            <Input
              type="date"
              {...register('date_of_birth', { 
                required: t('auth.dateOfBirthRequired', 'Data de nascimento é obrigatória'),
                validate: (value) => {
                  if (!value) return true // Let required validation handle empty values
                  const birthDate = new Date(value)
                  const today = new Date()
                  const age = today.getFullYear() - birthDate.getFullYear()
                  const monthDiff = today.getMonth() - birthDate.getMonth()
                  
                  if (age < 18 || (age === 18 && monthDiff < 0)) {
                    return t('auth.ageRequirement', 'Deve ter pelo menos 18 anos')
                  }
                  return true
                }
              })}
              error={errors.date_of_birth?.message}
            />
              </div>
            {/* Country/Nationality */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">
                {t('auth.country', 'País')}
              </label>
              <Select
                options={worldCountries.map(country => ({
                  value: country.nationality,
                  label: getCountryName(country, i18n.language)
                }))}
                value={watch('nationality')}
                onChange={(value) => {
                  setValue('nationality', value, { shouldValidate: true, shouldDirty: true })
                  
                  // Auto-set user_type based on country
                  if (value === 'ST') {
                    // São Tomé e Príncipe - keep current selection or default to individual
                    const currentUserType = watch('user_type')
                    if (!currentUserType) {
                      setValue('user_type', 'individual', { shouldValidate: false, shouldDirty: true })
                    }
                  } else {
                    // Any other country - set to tourist
                    setValue('user_type', 'tourist', { shouldValidate: false, shouldDirty: true })
                    // Show info toast for user
                    toast.success(t('auth.userTypeSetToTourist', 'Tipo de usuário definido como Turista Internacional'))
                  }
                }}
                placeholder={t('auth.selectCountry', 'Selecione o país')}
                error={errors.nationality?.message}
                disabled={isLoading}
              />
              {/* Hidden input for form validation */}
              <input
                type="hidden"
                {...register('nationality', { 
                  required: t('auth.countryRequired', 'País é obrigatório')
                })}
              />
            </div>

            {/* User Type */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-text-primary">
                {t('auth.userType', 'Tipo de Usuário')}
              </label>
              <RadioGroup
                name="user_type"
                value={watch('user_type') || ''}
                onChange={(value) => setValue('user_type', value as 'individual' | 'business' | 'tourist', { shouldValidate: true })}
                options={[
                  {
                    value: 'individual',
                    label: t('auth.individual', 'Usuário Individual')
                  },
                  {
                    value: 'business',
                    label: t('auth.business', 'Comerciante/Empresa')
                  },
                  {
                    value: 'tourist',
                    label: t('auth.tourist', 'Turista Internacional')
                  }
                ]}
                disabled={isLoading}
                error={errors.user_type?.message}
              />
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">
                {t('auth.password', 'Senha')}
              </label>
              <Input
                type="password"
                placeholder={t('auth.password', 'Senha')}
                {...register('password', { 
                  required: t('auth.passwordRequired', 'Senha é obrigatória'),
                  minLength: { value: 6, message: t('auth.passwordLength', 'Senha deve ter pelo menos 6 caracteres') }
                })}
                error={errors.password?.message}
                disabled={isLoading}
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">
                {t('auth.confirmPassword', 'Confirmar Senha')}
              </label>
              <Input
                type="password"
                placeholder={t('auth.confirmPassword', 'Confirmar Senha')}
                {...register('password_confirmation', { 
                  required: t('auth.confirmPasswordRequired', 'Confirmação de senha é obrigatória'),
                  validate: (value) => value === password || t('auth.passwordMismatch', 'Senhas não coincidem')
                })}
                error={errors.password_confirmation?.message}
                disabled={isLoading}
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full mobile-button mt-6"
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? t('auth.creatingAccount') : t('common.next')}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-text-secondary">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link 
                to="/login" 
                className="text-primary font-medium hover:underline"
              >
                {t('auth.login')}
              </Link>
            </p>
          </div>

          {/* Terms */}
          <div className="mt-4 text-center">
            <p className="text-xs text-text-secondary">
              {t('auth.termsAgreement')}{' '}
              <button 
                onClick={() => setIsTermsModalOpen(true)}
                className="text-primary hover:underline cursor-pointer"
              >
                {t('auth.termsOfUse')}
              </button>{' '}
              {t('common.and')}{' '}
              <button 
                onClick={() => setIsPrivacyModalOpen(true)}
                className="text-primary hover:underline cursor-pointer"
              >
                {t('auth.privacyPolicy')}
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
      </div>

      {/* Terms Modal */}
      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
      />

      {/* Privacy Modal */}
      <PrivacyModal 
        isOpen={isPrivacyModalOpen} 
        onClose={() => setIsPrivacyModalOpen(false)} 
      />

      {/* KYC Welcome Modal */}
      <KYCWelcomeModal
        isOpen={isKYCModalOpen}
        onClose={handleKYCClose}
        onComplete={handleKYCComplete}
      />

      <BusinessOnboardingModal
        isOpen={isBusinessModalOpen}
        onClose={() => {
          setIsBusinessModalOpen(false)
          setTimeout(() => {
            navigate('/')
          }, 100)
        }}
        onComplete={() => {
          setIsBusinessModalOpen(false)
          setTimeout(() => {
            navigate('/')
          }, 100)
        }}
      />
    </div>
  )
}

export function RegisterPage() {
  return (
    <ErrorBoundary>
      <RegisterPageContent />
    </ErrorBoundary>
  )
}



